import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, Users } from "lucide-react";

export default function PlatformUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (roleFilter) params.append("role", roleFilter);
      if (tenantFilter) params.append("tenant_id", tenantFilter);
      params.append("limit", "500");
      const { data } = await api.get(`/platform/users?${params.toString()}`);
      setUsers(data.users || []);
      setRoles(data.roles || []);
      const orgMap = {};
      (data.users || []).forEach((u) => { if (u.organization_id) orgMap[u.organization_id] = u.organization_name || u.organization_id; });
      setOrgs(Object.entries(orgMap).map(([id, name]) => ({ id, name })));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load users"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    setUpdating(u.id);
    try {
      await api.patch(`/platform/users/${u.id}/role`, { role: newRole });
      toast.success(`${u.email} → ${newRole}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    finally { setUpdating(null); }
  };

  const deactivate = async (u) => {
    if (!confirm(`Deactivate ${u.email}?`)) return;
    try {
      await api.delete(`/platform/users/${u.id}`);
      toast.success("User deactivated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Deactivate failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono mb-2">Platform User Management</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Assign roles & organizations.</h1>
        <p className="mt-2 text-sm text-stone-600">Promote, demote, and route any user in the platform. Every change is logged in the Audit Log.</p>
      </div>

      <div className="card-flat">
        <div className="grid gap-3 md:grid-cols-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-3 h-4 w-4 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search email or name…" data-testid="user-search" className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 pl-9 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" />
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className={inputCls}>
            <option value="">All roles</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={load} disabled={loading} className="btn-primary !text-xs">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} Search
          </button>
        </div>
      </div>

      <div className="card-flat">
        <div className="label-mono mb-3">Users ({users.length})</div>
        <div className="overflow-x-auto rounded-md border border-stone-200">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left">
              <tr className="border-b border-stone-200">
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Name / Email</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Organization</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Role</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">2FA</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Joined</th>
                <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-stone-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {users.map((u) => (
                <tr key={u.id} className={u.is_deactivated ? "opacity-50" : ""}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-950">{u.full_name}</div>
                    <div className="mt-0.5 font-mono text-[11px] text-stone-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-700">{u.organization_name || <span className="text-stone-400">—</span>}</td>
                  <td className="px-4 py-3">
                    <select
                      value={u.role}
                      disabled={updating === u.id}
                      onChange={(e) => changeRole(u, e.target.value)}
                      data-testid={`role-select-${u.id}`}
                      className="rounded-md border border-stone-300 bg-white px-2 py-1 font-mono text-xs uppercase text-slate-950 focus:border-slate-900"
                    >
                      {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] uppercase">{u.totp_enabled ? <span className="text-emerald-600">enabled</span> : <span className="text-stone-400">off</span>}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-stone-500 whitespace-nowrap">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    {!u.is_deactivated && (
                      <button onClick={() => deactivate(u)} className="btn-ghost !text-xs !text-red-600">Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-xs text-stone-500">No users match filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";
